import type { Drone, ProductSpecification } from '@/types/drones';

/**
 * Converte specifications do formato GitHub para o formato do app
 * Suporta tanto o formato antigo (apenas value) quanto o novo (label + value)
 */
function convertSpecificationsFromGitHub(
  specs:
    | Record<string, { label: Record<string, string>; value: Record<string, string> }>
    | undefined
): ProductSpecification[] | undefined {
  if (!specs || typeof specs !== 'object') return undefined;

  const converted = Object.entries(specs).map(([key, spec], index) => {
    // Verifica se é o formato novo (com label e value separados)
    if (spec && typeof spec === 'object' && 'label' in spec && 'value' in spec) {
      return {
        id: key,
        label: spec.label as { 'pt-BR': string; 'en-US': string; 'es-ES': string },
        value: spec.value as { 'pt-BR': string; 'en-US': string; 'es-ES': string },
        order: index,
      };
    }

    // Formato antigo (spec é o próprio value, sem label)
    return {
      id: key,
      label: spec as { 'pt-BR': string; 'en-US': string; 'es-ES': string },
      value: spec as { 'pt-BR': string; 'en-US': string; 'es-ES': string },
      order: index,
    };
  }) as ProductSpecification[];

  return converted.length > 0 ? converted : undefined;
}

interface GitHubFileResponse {
  content: string;
  sha: string;
  path: string;
}

interface GitHubDrone extends Omit<Drone, 'specifications'> {
  specifications?: Record<string, { label: Record<string, string>; value: Record<string, string> }>;
}

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || 'http://localhost:8787';

class GitHubService {
  /**
   * Obtém o conteúdo de um arquivo do repositório
   */
  async getFile(path: string): Promise<GitHubFileResponse | null> {
    try {
      const url = `${API_BASE_URL}/api/github/file?path=${encodeURIComponent(path)}`;
      const response = await fetch(url);

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data.file;
    } catch (error) {
      console.error('Error fetching file via proxy:', error);
      throw error;
    }
  }

  /**
   * Lista todos os produtos de uma categoria
   */
  async listProducts(category: string): Promise<Drone[]> {
    try {
      const url = `${API_BASE_URL}/api/github/products/${category}`;
      const response = await fetch(url);

      if (response.status === 404) {
        return [];
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();
      const products = data.products || [];

      // Convert specifications from GitHub format to app format
      return products.map((product: GitHubDrone) => ({
        ...product,
        specifications: product.specifications
          ? convertSpecificationsFromGitHub(product.specifications)
          : undefined,
      })) as Drone[];
    } catch (error) {
      console.error('Error listing products via proxy:', error);
      return [];
    }
  }

  /**
   * Busca um produto específico pelo slug
   */
  async getProduct(category: string, slug: string): Promise<Drone | null> {
    try {
      const path = `data/products/${category}/${slug}.json`;
      const file = await this.getFile(path);

      if (!file) {
        return null;
      }

      const content = decodeURIComponent(escape(atob(file.content)));
      const product = JSON.parse(content);

      // Convert specifications from GitHub format to app format
      if (product.specifications) {
        product.specifications = convertSpecificationsFromGitHub(product.specifications);
      }
      return product;
    } catch (error) {
      console.error('Error fetching product via proxy:', error);
      return null;
    }
  }
}

export const githubService = new GitHubService();
